import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import StudioShell from "@/components/StudioShell";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { MoodboardCard } from "@/components/MoodboardCard";

export default function RoomMoodboards() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [regenIdx, setRegenIdx] = useState(null);
  const [imgIdx, setImgIdx] = useState(null);
  const [busyTile, setBusyTile] = useState({});
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => api.get(`/projects/${id}`).then(r => setProject(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!project) return <StudioShell><div className="p-12">Loading…</div></StudioShell>;
  const boards = project.moodboards || [];
  const consistency = project.consistency || [];
  const flagged = consistency.filter(c => c.status !== "consistent").length;

  const regen = async (i, brief) => {
    setRegenIdx(i);
    try {
      await api.post(`/projects/${id}/regenerate-room/${i}`, brief !== undefined ? { brief } : {});
      await load();
      toast.success(brief !== undefined ? "Room re-composed around your ideas" : "Room regenerated");
    } catch (e) { toast.error(apiError(e, "Failed to regenerate")); }
    setRegenIdx(null);
  };

  const regenImage = async (i, tile = 0) => {
    if (tile === 0) setImgIdx(i); else setBusyTile({ room: i, tile });
    try {
      await api.post(`/projects/${id}/regenerate-image/${i}?tile=${tile}`);
      await load();
      toast.success(`New option rendered for view ${tile + 1}`);
    } catch (e) { toast.error(apiError(e, "Image generation unavailable — try again")); }
    setImgIdx(null); setBusyTile({});
  };

  const saveText = async (i, patch) => {
    try {
      await api.patch(`/projects/${id}/moodboards/${i}`, patch);
      if (patch.image_count !== undefined) { await load(); toast.success(`${patch.image_count} image${patch.image_count > 1 ? "s" : ""} on this slide`); return; }
      setProject(p => ({ ...p, moodboards: p.moodboards.map((m, j) => j === i ? { ...m, ...patch } : m) }));
      toast.success(patch.gallery ? "Arrangement saved" : patch.layout ? `Layout: ${patch.layout}` : "Saved");
    } catch (e) { toast.error(apiError(e, "Failed to save")); }
  };

  const runCheck = async () => {
    setChecking(true);
    try {
      const r = await api.post(`/projects/${id}/consistency-check`);
      setProject(p => ({ ...p, consistency: r.data.consistency }));
      toast.success("Consistency check complete");
    } catch { toast.error("Consistency check failed"); }
    setChecking(false);
  };

  const generatePresentation = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${id}/generate-presentation`);
      navigate(`/project/${id}/presentation`);
    } catch { toast.error("Failed to generate presentation"); }
    setBusy(false);
  };

  return (
    <StudioShell projectContext={project}>
      <div className="px-6 sm:px-12 py-12 max-w-[1440px] mx-auto">
        <div className="flex flex-wrap items-end justify-between mb-8 gap-4">
          <div>
            <div className="eyebrow mb-3">Room Mood Boards</div>
            <h1 className="editorial-heading text-4xl sm:text-5xl">{project.name}</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={runCheck} disabled={checking || boards.length === 0} data-testid="mb-consistency-check" className="inline-flex items-center gap-2 px-5 py-3 border border-stone-300 text-sm hover:border-black disabled:opacity-50">
              <ShieldCheck size={16} className={checking ? "animate-pulse" : ""} /> {checking ? "Checking…" : "Run DNA Consistency Check"}
            </button>
            <button onClick={generatePresentation} disabled={busy || boards.length === 0} data-testid="mb-generate-presentation" className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-sm disabled:opacity-50">
              Generate Client Presentation <ArrowRight size={16} />
            </button>
          </div>
        </div>

        {consistency.length > 0 && (
          <div data-testid="consistency-summary" className={`mb-12 px-5 py-4 border text-sm flex items-center gap-3 ${flagged ? "border-amber-400 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`}>
            <ShieldCheck size={16} />
            {flagged ? `${flagged} of ${consistency.length} rooms deviate from the approved Design DNA — review the flags below.` : `All ${consistency.length} rooms are consistent with the approved Design DNA.`}
          </div>
        )}

        {boards.length === 0 ? (
          <div className="studio-card p-12 text-center text-stone-500">No mood boards yet. Return to Design DNA and generate them.</div>
        ) : (
          <div className="space-y-16">
            {boards.map((mb, i) => (
              <MoodboardCard
                key={`${mb.room}-${i}`} index={i} mb={mb} project={project}
                check={consistency.find(c => c.index === i)}
                regenerating={regenIdx === i} imageBusy={imgIdx === i}
                onRegen={(brief) => regen(i, brief)} onRegenImage={() => regenImage(i, 0)} onRegenTile={(tile) => regenImage(i, tile)}
                busyTile={busyTile.room === i ? busyTile.tile : (imgIdx === i ? 0 : null)}
                onSave={(patch) => saveText(i, patch)}
              />
            ))}
          </div>
        )}
      </div>
    </StudioShell>
  );
}
